/**
 * AI credits — grant sizes, per-action costs and daily caps. The ONE place
 * these numbers live; imported by the API routes for enforcement and
 * client-side for display copy.
 *
 * Why credits are bundled into Pro (2026-08-28): Pro shipped with generous
 * per-feature daily quotas for the Modal-backed tools (HQ separation 15/day,
 * Loop Forge 100/day, 替え歌 30/day, mastering 20/day) but with ZERO credits —
 * and AI music generation is credit-only. A paying Pro member who used the
 * AI assistant's one-click generation therefore burned the 30-credit signup
 * bonus and then hit a 402 while subscribed. Measured over the 60 days to
 * 2026-08-28: 28 of 30 recent generations went through the expensive `base`
 * model, and the heaviest single user ran 18 in 14 days — i.e. ~24 days to
 * exhaust the bonus. This closes that hole.
 *
 * Cost basis (measured 2026-08-28 against the live Modal endpoints, A10G at
 * $0.000306/s + CPU/mem ≈ $0.00046/s of container time):
 *   turbo 30s  →  4.7s GPU   |  turbo 240s → 20.0s GPU
 *   base  30s  → 11.8-15.8s GPU
 *   cold container boot ≈ 87-102s, scaledown_window 60s
 * So an ISOLATED generation costs ~$0.07 (boot+idle dominated) while the
 * marginal one inside a warm window costs $0.002-0.006. Credit weights below
 * sit between those two regimes on purpose: strict proportionality to
 * inference time would overcharge long/base jobs, since the constant boot
 * cost is the same for a 30s turbo clip and a 4-minute base track.
 */

/** Credits granted per month to an active/trialing Pro subscriber. */
export const PRO_MONTHLY_CREDITS = 100;

/** Length of a Pro credit month, in ms. See proCreditPeriodIndex(). */
export const PRO_CREDIT_PERIOD_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Which 30-day allowance window a moment falls into, counted from the START
 * of the subscription's current Stripe billing period.
 *
 * Two things have to be true at once, and this is the anchor that gets both:
 *   - MONTHLY plans must yield exactly one allowance per billing period.
 *     `current_period_start` advances every month, so the index is always 0
 *     within a period and a new period mints a fresh key.
 *   - ANNUAL plans must still yield one allowance a month, not one a year.
 *     Their period start is fixed for 12 months, so the index walks 0…12 and
 *     each step mints its own key.
 *
 * `subscriptions.created_at` was the obvious anchor and is wrong: the table
 * is upserted on user_id, so that column records when the user FIRST ever had
 * any subscription row — including a Pro Pass. Anchoring there meant someone
 * who cancelled and resubscribed got nothing until their old anniversary came
 * round, while a pass holder who subscribed on day 29 collected two
 * allowances within a day.
 */
export function proCreditPeriodIndex(
  periodStartMs: number,
  periodEndMs: number,
  nowMs: number,
): number {
  const raw = Math.max(0, Math.floor((nowMs - periodStartMs) / PRO_CREDIT_PERIOD_MS));
  // Clamp to the number of whole allowances this billing period is worth.
  // Without it a 31-day month pays out three times: p0 on day 0, p1 on day 30
  // (the period hasn't rolled yet), then p0 of the new period on day 31.
  //   31-day month  → floor(31/30) - 1 = 0  → one allowance
  //   28-day month  → clamped to 0          → one allowance
  //   7-day trial   → clamped to 0          → one allowance
  //   365-day year  → floor(365/30) - 1 = 11 → twelve allowances
  const periodLength = periodEndMs - periodStartMs;
  if (!Number.isFinite(periodLength) || periodLength <= 0) return raw;
  const maxIndex = Math.max(0, Math.floor(periodLength / PRO_CREDIT_PERIOD_MS) - 1);
  return Math.min(raw, maxIndex);
}

/**
 * Idempotency key for one user's allowance in one 30-day window.
 *
 * Keyed by USER, not by subscription: trial eligibility is a check-then-act,
 * so someone can open several checkout sessions before any completes and end
 * up with more than one Stripe subscription. The local subscriptions table
 * holds one row per user, so this key can't be multiplied that way.
 */
export function proCreditGrantKey(userId: string, periodStartMs: number, periodIndex: number): string {
  return `${userId}:${Math.floor(periodStartMs / 1000)}:p${periodIndex}`;
}

/**
 * Credits granted with a one-time Pro Pass, keyed by the pass length in days
 * (create-pass-checkout writes `days` into the session metadata).
 * The 30-day pass (¥980) matches a paid month; the 24h pass (¥200) gets a
 * day's worth at the same daily cap.
 */
export const PASS_CREDITS_BY_DAYS: Readonly<Record<number, number>> = { 1: 20, 30: 100 };

/** Fallback when a pass carries an unexpected `days` value. */
export const PASS_CREDITS_DEFAULT = 20;

/**
 * Generations per UTC day, by plan. This is an abuse ceiling, not the
 * product limit — the credit balance is what users actually spend. Counting
 * generations (not credits) here lets us reuse the atomic
 * loopforge_consume/refund RPCs unchanged, which consume exactly one unit.
 */
export const MUSICGEN_DAILY_LIMITS = {
  /** Logged in, no active/trialing subscription. */
  free: 10,
  /** subscriptions.status in ('active','trialing') — mirrors derivePlan. */
  pro: 20,
} as const;

export type MusicGenPlan = keyof typeof MUSICGEN_DAILY_LIMITS;

/**
 * Identity prefix for the shared loopforge_usage counter table
 * ('aicr:user:<uuid>'), namespacing these counters away from Loop Forge's.
 */
export const AI_CREDIT_IDENTITY_PREFIX = 'aicr:';

/** Audio longer than this counts as a long generation (see weights below). */
export const MUSICGEN_LONG_SECONDS = 60;

/** credit_transactions reasons. */
export const PRO_MONTHLY_GRANT_REASON = 'pro_monthly_credits';
export const PASS_GRANT_REASON = 'pass_credits';
export const MUSICGEN_CONSUME_REASON = 'music_generation';
export const MUSICGEN_REFUND_REASON = 'refund';

/**
 * Credits per generation. `base` is the highest-quality XL deployment (~3x
 * the GPU time of turbo at the same duration) and is what the AI assistant's
 * one-click flow always uses.
 */
export const MUSICGEN_CREDIT_COSTS = {
  turbo: 1,
  turboLong: 2,
  base: 3,
  baseLong: 5,
} as const;

/** Credits a single music generation costs. Server-authoritative. */
export function musicGenCreditCost(opts: { useBase: boolean; duration: number }): number {
  const long = opts.duration > MUSICGEN_LONG_SECONDS;
  if (opts.useBase) return long ? MUSICGEN_CREDIT_COSTS.baseLong : MUSICGEN_CREDIT_COSTS.base;
  return long ? MUSICGEN_CREDIT_COSTS.turboLong : MUSICGEN_CREDIT_COSTS.turbo;
}

/**
 * Pro members buy top-up credits at a discount: heavy users pay for their own
 * extra GPU time instead of being throttled, which keeps the margin intact as
 * usage grows. 20% off the public pack price.
 */
export const PRO_TOPUP_DISCOUNT = 0.2;

/** Apply the Pro top-up discount to a pack price (returns whole units). */
export function proTopUpPrice(price: number): number {
  return Math.round(price * (1 - PRO_TOPUP_DISCOUNT));
}
